// /changelog — every product update, newest first, on a timeline. Entries come from Firestore
// (changelogEntries.js); scripts/changelog.js publishes them.
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CtaBand from '../site/CtaBand';
import Seo from '../site/Seo';
import { formatPostDate } from '../news/posts';
import { DATA_SCRIPT_ID, TYPE_LABELS, cachedEntries, fetchEntries, serializeEntries } from './changelogEntries';
import './changelog.css';

// Links to bumpups.com stay in the app; other sites open in a new tab
const MarkdownLink = ({ node, href = '', children }) => {
  const path = href.replace(/^https?:\/\/(www\.)?bumpups\.com(?=\/|$)/, '') || (href && '/');
  if (path.startsWith('/') && !path.startsWith('//')) return <Link to={path}>{children}</Link>;
  if (/^https?:/.test(href)) return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
  return <a href={href}>{children}</a>;
};

// Entry titles are h2, so headings inside an entry start at h3
const Heading = ({ node, ...props }) => <h3 {...props}>{props.children}</h3>;

const MARKDOWN_COMPONENTS = { a: MarkdownLink, h1: Heading, h2: Heading };

const Entry = ({ entry }) => (
  <li className="cl-entry" id={entry.slug}>
    <a href={`#${entry.slug}`} className="cl-date" title="Link to this update">
      <time dateTime={entry.date}>{formatPostDate(entry.date)}</time>
    </a>
    <div className="cl-content">
      <h2 className="cl-title">{entry.title}</h2>
      {entry.types.length > 0 && (
        <ul className="cl-types" aria-label="Type of update">
          {entry.types.map(type => <li key={type} className={`cl-type cl-type--${type}`}>{TYPE_LABELS[type]}</li>)}
        </ul>
      )}
      {entry.body && (
        <div className="cl-md">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS} disallowedElements={['img']} skipHtml>
            {entry.body}
          </ReactMarkdown>
        </div>
      )}
    </div>
  </li>
);

const Placeholder = () => (
  <ol className="cl-list" aria-hidden="true">
    {[0, 1, 2].map(i => (
      <li key={i} className="cl-entry">
        <span className="cl-date"><span className="cl-bone cl-bone--date" /></span>
        <div className="cl-content">
          <span className="cl-bone cl-bone--title" />
          <span className="cl-bone" />
          <span className="cl-bone" />
          <span className="cl-bone cl-bone--short" />
        </div>
      </li>
    ))}
  </ol>
);

export default function Changelog() {
  const [entries, setEntries] = useState(cachedEntries);
  const [failed, setFailed] = useState(false);
  const { hash } = useLocation();
  const jumped = useRef(false);

  useEffect(() => {
    let active = true;
    fetchEntries()
      .then(fresh => { if (active) setEntries(fresh); })
      .catch(err => {
        console.error('Changelog failed to load', err);
        if (active) setFailed(true);
      });
    return () => { active = false; };
  }, []);

  // A link to one update (/changelog#slug) scrolls to it once the entries are on the page
  useEffect(() => {
    if (jumped.current || !entries || !hash) return;
    jumped.current = true;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target) target.scrollIntoView();
  }, [entries, hash]);

  const loading = !entries && !failed;

  return (
    <>
      <Seo
        title="Bumpups | Changelog"
        description="Every new feature, improvement and fix in Bumpups, newest first."
      />

      <section className="cl-hero mk-hero-bg">
        <div className="mk-container cl-wrap">
          <h1 className="mk-h1">Changelog</h1>
          <p className="mk-lead">New features, improvements and fixes in Bumpups.</p>
        </div>
      </section>

      {/* aria-busy also tells scripts/prerender.js to wait for the entries */}
      <section className="mk-container cl-wrap cl-log" aria-busy={loading || undefined}>
        {loading && <Placeholder />}
        {!entries && failed && (
          <p className="cl-status" data-load-error>The changelog didn't load. Check your connection, then refresh the page.</p>
        )}
        {entries && entries.length === 0 && <p className="cl-status">No updates yet.</p>}
        {entries && entries.length > 0 && (
          <>
            <ol className="cl-list">
              {entries.map(entry => <Entry key={entry.slug} entry={entry} />)}
            </ol>
            <script type="application/json" id={DATA_SCRIPT_ID} dangerouslySetInnerHTML={{ __html: serializeEntries(entries) }} />
          </>
        )}
      </section>

      <CtaBand />
    </>
  );
}
